import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import * as Popover from '@radix-ui/react-popover'
import { FiAlertTriangle, FiArrowRight } from 'react-icons/fi'
import { PiArrowBendUpRightBold } from 'react-icons/pi'

import { REPORTS, STRIPE_SETUP } from 'modules/App/components/Header'
import { useCanSeeReports } from 'modules/Reports/useCanSeeReports'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { Tooltip } from 'common/components/Tooltip/Tooltip'
import { NO_LINK_HANDLE_SHARE_MESSAGE, PRODUCT_SHARE_GATE_MESSAGE } from 'modules/ProductInfo/constants/shareConstants'
import { buildShareLinks } from 'modules/ProductInfo/utils/shareLinks'

// Shows the Attribution line only to users who can open the Attribution page.
export function ReportsLinkLine({ className = '' }) {
  const canSeeReports = useCanSeeReports()
  if (!canSeeReports) return null
  return (
    <p className={`text-xs text-tGray-dark ${className}`}>
      Need a link for a specific campaign?{' '}
      <Link to={REPORTS} className='text-tGreen font-semibold underline'>
        Make one in Attribution.
      </Link>
    </p>
  )
}

// One product's share links. The product builder's share menu and the Share page both
// render this, so they always offer the same links.
export function ShareLinks({ coachSlug, pagePath, title = 'Share this page', showReportsLine = true }) {
  const { createAlert } = useAlert()
  const links = buildShareLinks({ coachSlug, pagePath })

  return (
    <div className='flex flex-col'>
      <p className='font-bold text-tBlack text-sm mb-1'>{title}</p>
      <p className='text-xs text-tGray-dark mb-3'>Use the link that matches where you post it.</p>
      <ul className='flex flex-col gap-3' aria-label={`${title} links`}>
        {links.map((link) => (
          <li key={link.id} className='flex items-center justify-between gap-3'>
            <p className='text-sm font-semibold text-tBlack'>
              {link.label}
              {link.id === 'plain' && <span className='font-normal text-tGray-dark'>, no attribution</span>}
            </p>
            <button
              type='button'
              aria-label={`Copy ${link.label}`}
              title={link.url}
              className='flex-shrink-0 text-xs font-semibold text-tGreen border-2 border-gray-200 rounded-lg px-2 py-1 hover:border-tGreen transition-colors'
              onClick={() =>
                navigator.clipboard.writeText(link.url).then(() => {
                  createAlert({ text: 'Copied to clipboard', type: 'success' })
                })
              }
            >
              Copy
            </button>
          </li>
        ))}
      </ul>
      {showReportsLine && <ReportsLinkLine className='mt-4 pt-3 border-t border-gray-200' />}
    </div>
  )
}

// A compact "Share" button for cards and page headers, where the link itself is one click away
// as the menu's plain link. Its hover stays on the button, since the card around it has its own.
// Until the link can be shared it becomes a solid "Finish setup" button that opens the page where
// the missing step is done, or, when that page is the one already open, an inactive "Publish to share".
const BUTTON_BASE =
  'inline-flex items-center gap-[7px] rounded-lg border px-3 py-[7px] text-[13px] font-bold cursor-pointer transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2fb597]'
const SHARE_BUTTON_CLASSES = `${BUTTON_BASE} border-[#dcdfe5] bg-white text-[#1c2240] hover:border-[#2fb597] hover:bg-[#eefaf5] hover:text-[#177a5f] active:bg-[#e3f6ef]`
const BLOCKED_BUTTON_CLASSES = `${BUTTON_BASE} border-[#1c2240] bg-[#1c2240] text-white hover:bg-[#2a3156] hover:border-[#2a3156]`
const INACTIVE_BUTTON_CLASSES = `${BUTTON_BASE} border-[#dcdfe5] bg-[#f3f4f6] text-gray-500 cursor-not-allowed`

// The pill that shows a product's link. Once the product is ready for sale and the coach has a
// link handle it opens the share menu; before that it only explains why the link can't be shared yet.
// blockedReason replaces that explanation for pages with their own condition, like publishing.
// The element prop is `tag`, not `as`: twin's styled wrapper consumes `as` and would
// render a bare element instead of this menu.
export function ShareLinkMenu({
  coachSlug,
  pagePath,
  isReadyForSale = true,
  blockedReason = null,
  tag: Tag = 'button',
  triggerRef,
  className = '',
  iconClassName = 'text-black group-hover:text-tGreen',
  variant = 'pill',
  setupHref = null,
  children,
}) {
  const isButton = variant === 'button'
  const { createAlert } = useAlert()
  const navigate = useNavigate()
  const tagProps = Tag === 'button' ? { type: 'button' } : { role: 'button', tabIndex: 0 }
  const blockedMessage = blockedReason
    ? blockedReason
    : !isReadyForSale
    ? PRODUCT_SHARE_GATE_MESSAGE : !coachSlug ? NO_LINK_HANDLE_SHARE_MESSAGE : null

  if (blockedMessage && isButton) {
    // The link handle is always set in Sell setup, whatever page the button sits on
    const href = !blockedReason && isReadyForSale && !coachSlug ? STRIPE_SETUP : setupHref
    if (!href) {
      return (
        <Tag
          ref={triggerRef}
          data-share-trigger={true}
          {...tagProps}
          aria-disabled={true}
          aria-label={blockedMessage}
          className={`relative ${INACTIVE_BUTTON_CLASSES} ${className}`}
        >
          Publish to share
          <Tooltip content={blockedMessage} triggerClasses='absolute inset-0 z-[5]' />
        </Tag>
      )
    }
    return (
      <Tag
        ref={triggerRef}
        data-share-trigger={true}
        {...tagProps}
        aria-label={blockedMessage}
        className={`relative ${BLOCKED_BUTTON_CLASSES} ${className}`}
        onClick={() => navigate(href)}
        onKeyDown={(e) => {
          if (Tag !== 'button' && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            navigate(href)
          }
        }}
      >
        Finish setup
        <FiArrowRight aria-hidden={true} className='w-3.5 h-3.5' />
        <Tooltip content={blockedMessage} triggerClasses='absolute inset-0 z-[5]' />
      </Tag>
    )
  }

  if (blockedMessage) {
    return (
      <Tag
        ref={triggerRef}
        data-share-trigger={true}
        {...tagProps}
        aria-label={blockedMessage}
        className={`relative group ${className}`}
        onClick={() => createAlert({ text: blockedMessage, type: 'danger' })}
      >
        {children}
        <FiAlertTriangle className='text-orange-400 ml-2 flex-shrink-0' />
        <Tooltip content={blockedMessage} triggerClasses='absolute inset-0 z-[5]' />
      </Tag>
    )
  }

  return (
    <Popover.Root>
      <Popover.Trigger asChild={true}>
        <Tag
          ref={triggerRef}
          data-share-trigger={true}
          {...tagProps}
          aria-label='Share product link'
          className={`relative group ${isButton ? SHARE_BUTTON_CLASSES : ''} ${className}`}
        >
          {isButton ? 'Share' : children}
          {/* The button's icon takes its color from the text, so both change together on the
              button itself rather than on whatever card around it is also a hover group */}
          {isButton ? (
            <PiArrowBendUpRightBold aria-hidden={true} className='w-3.5 h-3.5 flex-shrink-0' />
          ) : (
            <PiArrowBendUpRightBold className={`ml-2 w-3.5 h-3.5 flex-shrink-0 transition-colors ${iconClassName}`} />
          )}
        </Tag>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className='bg-white p-4 rounded-xl w-[340px] max-w-[calc(100vw-32px)] shadow-xl border-2 border-gray-200 z-[999]'
          align='start'
          sideOffset={6}
          aria-label='Share links'
        >
          <ShareLinks coachSlug={coachSlug} pagePath={pagePath} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
